// Lightweight session-state loader for views that DON'T need chat / voice /
// polling. Reads `discoveryFeatures` (read-only) so the Delivery view can
// avoid the full useSession surface.
//
// Lives next to useSession so the wire shape stays in one place — if the
// /sessions/:id GET payload changes, both hooks pick it up.

import { useCallback, useEffect, useState } from 'react';
import type { DiscoveryFeaturesPayload } from './sessionTypes';

interface SessionMeta {
  exists: boolean;
  loading: boolean;
  error: string | null;
}

export interface UseSessionFeaturesReturn extends SessionMeta {
  discoveryFeatures: DiscoveryFeaturesPayload;
  refresh: () => Promise<void>;
}

const EMPTY_FEATURES: DiscoveryFeaturesPayload = {
  features: [],
  extractedAt: null,
  statementCountAtExtraction: null,
  warnings: [],
  status: 'idle',
};

export function useSessionFeatures(sessionId: string | null): UseSessionFeaturesReturn {
  const [discoveryFeatures, setDiscoveryFeatures] = useState<DiscoveryFeaturesPayload>(EMPTY_FEATURES);
  const [loading, setLoading] = useState(false);
  const [exists, setExists] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/discovery-features`);
      if (res.status === 404) {
        setExists(false);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setExists(true);
      setDiscoveryFeatures({
        features: data.features || [],
        extractedAt: data.extractedAt ?? null,
        statementCountAtExtraction: data.statementCountAtExtraction ?? null,
        warnings: data.warnings || [],
        status: data.status || 'idle',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { discoveryFeatures, refresh, loading, exists, error };
}
